import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Hub } from 'aws-amplify/utils'
import {
  cognitoSignIn,
  cognitoSignUp,
  cognitoSignOut,
  cognitoConfirmSignUp,
  cognitoResendCode,
  cognitoResetPassword,
  cognitoConfirmResetPassword,
  cognitoGetCurrentUser,
  cognitoGetAccessToken,
  cognitoIsAuthenticated,
  type CognitoUser,
} from './cognito'
import { api, setTokens, clearTokens } from '@/lib/api/client'
import { ENDPOINTS } from '@/lib/api/endpoints'
import { queryKeys } from '@/lib/query/client'
import { disableDemoMode } from '@/lib/demo'
import { useFiltersStore } from '@/stores/filters.store'
import type { AuthUser } from '@/types'

type AuthStep =
  | 'idle'
  | 'signIn'
  | 'signUp'
  | 'confirmSignUp'
  | 'resetPassword'
  | 'confirmResetPassword'

interface AuthState {
  user: AuthUser | null
  cognitoUser: CognitoUser | null
  isLoading: boolean
  isAuthenticated: boolean
  step: AuthStep
  error: string | null
  pendingEmail: string | null
}

interface AuthContextType extends AuthState {
  // Sign In - returns true if login was successful
  signIn: (email: string, password: string) => Promise<boolean>

  // Sign Up
  signUp: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    companyName: string
  }) => Promise<void>
  confirmSignUp: (code: string) => Promise<void>
  resendCode: () => Promise<void>

  // Password Reset
  resetPassword: (email: string) => Promise<void>
  confirmResetPassword: (code: string, newPassword: string) => Promise<void>

  // Sign Out
  signOut: () => Promise<void>

  // Utils
  refreshUser: () => Promise<void>
  clearError: () => void
  setStep: (step: AuthStep) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    cognitoUser: null,
    isLoading: true,
    isAuthenticated: false,
    step: 'idle',
    error: null,
    pendingEmail: null,
  })

  const queryClient = useQueryClient()

  // Fetch user details from our API
  const fetchUserFromApi = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const token = await cognitoGetAccessToken()
      if (!token) return null

      // Set token for API client
      setTokens(token)

      // Fetch user details from our API
      const response = await api.get<{ user: AuthUser }>(ENDPOINTS.auth.me)
      return response.user
    } catch {
      return null
    }
  }, [])

  // Check and restore auth state on mount
  const checkAuthState = useCallback(async () => {
    try {
      const isAuth = await cognitoIsAuthenticated()

      if (isAuth) {
        const cognitoUser = await cognitoGetCurrentUser()
        const user = await fetchUserFromApi()

        setState((prev) => ({
          ...prev,
          user,
          cognitoUser,
          isAuthenticated: !!user,
          isLoading: false,
        }))
      } else {
        clearTokens()
        setState((prev) => ({
          ...prev,
          user: null,
          cognitoUser: null,
          isAuthenticated: false,
          isLoading: false,
        }))
      }
    } catch {
      clearTokens()
      setState((prev) => ({
        ...prev,
        user: null,
        cognitoUser: null,
        isAuthenticated: false,
        isLoading: false,
      }))
    }
  }, [fetchUserFromApi])

  // Initial auth check
  useEffect(() => {
    checkAuthState()
  }, [checkAuthState])

  // Listen for Amplify auth events
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuthState()
          break
        case 'signedOut':
          clearTokens()
          setState((prev) => ({
            ...prev,
            user: null,
            cognitoUser: null,
            isAuthenticated: false,
          }))
          queryClient.clear()
          break
        case 'tokenRefresh':
          // Token was refreshed, update API client
          cognitoGetAccessToken().then((token) => {
            if (token) setTokens(token)
          })
          break
        case 'tokenRefresh_failure':
          // Token refresh failed, sign out
          cognitoSignOut()
          break
      }
    })

    return () => unsubscribe()
  }, [checkAuthState, queryClient])

  // Sign In - returns true if login was successful
  const signIn = async (email: string, password: string): Promise<boolean> => {
    setState((prev) => ({ ...prev, error: null, step: 'signIn' }))

    // Amplify refuses a fresh signIn when a stale session is still cached in
    // localStorage ("There is already a signed in user"). Wipe Cognito state
    // first so users aren't trapped in a login loop after a hung session.
    try {
      await cognitoSignOut()
    } catch {
      // ignore — no session to clear
    }
    clearTokens()

    try {
      const result = await cognitoSignIn(email, password)

      if (result.isSignedIn) {
        const cognitoUser = await cognitoGetCurrentUser()
        const user = await fetchUserFromApi()

        // Set authenticated based on Cognito success, even if API user fetch fails
        // This allows the app to work even if /auth/me is temporarily unavailable
        const isAuth = !!cognitoUser

        setState((prev) => ({
          ...prev,
          user,
          cognitoUser,
          isAuthenticated: isAuth,
          step: 'idle',
        }))

        queryClient.invalidateQueries({ queryKey: queryKeys.auth.all })
        return isAuth
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        setState((prev) => ({
          ...prev,
          step: 'confirmSignUp',
          pendingEmail: email,
        }))
        return false
      }
      return false
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign in failed'
      setState((prev) => ({ ...prev, error: message, step: 'idle' }))
      throw error
    }
  }

  // Sign Up
  const signUp = async (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    companyName: string
  }) => {
    setState((prev) => ({ ...prev, error: null, step: 'signUp' }))

    try {
      const result = await cognitoSignUp(data.email, data.password, {
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
      })

      if (result.isSignUpComplete) {
        // Auto sign-in after signup
        await signIn(data.email, data.password)
      } else {
        setState((prev) => ({
          ...prev,
          step: 'confirmSignUp',
          pendingEmail: data.email,
        }))
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign up failed'
      setState((prev) => ({ ...prev, error: message, step: 'idle' }))
      throw error
    }
  }

  // Confirm Sign Up
  const confirmSignUp = async (code: string) => {
    if (!state.pendingEmail) {
      throw new Error('No pending email for confirmation')
    }

    setState((prev) => ({ ...prev, error: null }))

    try {
      await cognitoConfirmSignUp(state.pendingEmail, code)
      setState((prev) => ({ ...prev, step: 'idle', pendingEmail: null }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Confirmation failed'
      setState((prev) => ({ ...prev, error: message }))
      throw error
    }
  }

  // Resend Confirmation Code
  const resendCode = async () => {
    if (!state.pendingEmail) {
      throw new Error('No pending email')
    }

    setState((prev) => ({ ...prev, error: null }))

    try {
      await cognitoResendCode(state.pendingEmail)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resend code'
      setState((prev) => ({ ...prev, error: message }))
      throw error
    }
  }

  // Reset Password
  const resetPassword = async (email: string) => {
    setState((prev) => ({ ...prev, error: null, step: 'resetPassword' }))

    try {
      await cognitoResetPassword(email)
      setState((prev) => ({
        ...prev,
        step: 'confirmResetPassword',
        pendingEmail: email,
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset failed'
      setState((prev) => ({ ...prev, error: message, step: 'idle' }))
      throw error
    }
  }

  // Confirm Reset Password
  const confirmResetPassword = async (code: string, newPassword: string) => {
    if (!state.pendingEmail) {
      throw new Error('No pending email for password reset')
    }

    setState((prev) => ({ ...prev, error: null }))

    try {
      await cognitoConfirmResetPassword(state.pendingEmail, code, newPassword)
      setState((prev) => ({ ...prev, step: 'idle', pendingEmail: null }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset confirmation failed'
      setState((prev) => ({ ...prev, error: message }))
      throw error
    }
  }

  // Sign Out
  const signOut = async () => {
    disableDemoMode()
    try {
      await cognitoSignOut()
    } catch {
      // Ignore sign out errors
    } finally {
      clearTokens()
      useFiltersStore.getState().resetFilters()
      setState((prev) => ({
        ...prev,
        user: null,
        cognitoUser: null,
        isAuthenticated: false,
        step: 'idle',
      }))
      queryClient.clear()
    }
  }

  // Refresh User
  const refreshUser = useCallback(async () => {
    const user = await fetchUserFromApi()
    const cognitoUser = await cognitoGetCurrentUser()
    setState((prev) => ({
      ...prev,
      user,
      cognitoUser,
      isAuthenticated: !!user,
    }))
  }, [fetchUserFromApi])

  // Clear Error - memoized to prevent infinite loops in useEffect
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  // Set Step - memoized to prevent infinite loops in useEffect
  const setStep = useCallback((step: AuthStep) => {
    setState((prev) => ({ ...prev, step }))
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signUp,
        confirmSignUp,
        resendCode,
        resetPassword,
        confirmResetPassword,
        signOut,
        refreshUser,
        clearError,
        setStep,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Legacy export for backward compatibility
export { useAuth as useAuthContext }
